import { envVar } from '@env'
import { useAuthState } from '@features/app-hooks'
import { formatAuthError } from '@features/auth'
import type { AuthError } from '@features/auth/auth-client-adapter'
import { loginWithEmailPassword, type PasswordResetEmailError } from '@features/auth/auth-module'
import { Link } from '@features/link'
import type { Organization } from '@st/sdk'
import { BrandLogo, Button, FullPageCard, TextInput } from '@st/theme'
import { getQueryParams, removeQueryParams } from '@st/util/url'
import { ReactNode, useState } from 'react'
import { P, match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { OrganizationLogo } from '../components/organization-logo'
import { OrganizationSelectorPage } from './organization-selector'

type Props = {
  header?: ReactNode
  onClickSignUp?: () => void
}

/**
 * The main entry point for the organization login page. Decides which sub-view to render
 * based on the authentication state.
 */
export function OrganizationLoginPage({ header, onClickSignUp }: Props) {
  const { status } = useAuthState()

  return match(status)
    .with('loggedIn', () => <OrganizationSelectorPage />)
    .with('unknown', () => <FullPageCard header={header} />)
    .with(P.union('loggedOut', 'loggingIn', 'waitingForMFACode', 'error'), () => (
      <FullPageCard>
        <OrganizationLoginForm header={header} onClickSignUp={onClickSignUp} />
      </FullPageCard>
    ))
    .exhaustive()
}

/**
 * Determines which login-related view to show:
 * - Reset password form (if token present)
 * - Login with password form (default)
 * - Within LoginWithPasswordForm, we will also handle MFA states, password reset requests
 */
function OrganizationLoginForm({
  header,
  onClickSignUp
}: {
  header: ReactNode
  onClickSignUp?: () => void
}) {
  const route = useProcess('route')

  const resetPasswordToken: ResetPaswordAction | undefined = useSelect('route', (s) => {
    return parseResetPasswordAction(s.location.pathname)
  })

  if (resetPasswordToken) {
    return (
      <ResetPasswordForm
        header={header}
        action={resetPasswordToken}
        onClickContinueToLogin={() => {
          route.send({
            type: 'navigate',
            pathname: resetPasswordToken.cleanedPath
          })
        }}
      />
    )
  }

  return <LoginWithPasswordForm header={header} onClickSignUp={onClickSignUp} />
}

type ResetPasswordState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'succeeded' }
  | { status: 'failed'; error: AuthError | 'missing_name' }

type ResetPaswordAction = {
  token: string
  mode: 'invite' | 'reset-password'
  cleanedPath: string
}

function parseResetPasswordAction(pathname: string): ResetPaswordAction | undefined {
  const searchParams = getQueryParams(pathname)
  if (!searchParams['reset-password-token']) {
    return undefined
  }

  return {
    token: searchParams['reset-password-token'],
    mode: match(searchParams['mode'])
      .with('invite', () => 'invite' as const)
      .otherwise(() => 'reset-password' as const),
    cleanedPath: removeQueryParams(pathname, ['reset-password-token', 'mode'])
  }
}

/**
 * Handles setting a new password using the reset-password-token.
 */
function ResetPasswordForm({
  header,
  action,
  onClickContinueToLogin
}: {
  header: ReactNode
  action: ResetPaswordAction
  onClickContinueToLogin: () => void
}) {
  const auth = useProcess('auth')
  const authState = useAuthState()
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [missingNameError, setMissingNameError] = useState(false)

  function onSubmit() {
    if (action.mode == 'invite' && !name) {
      setMissingNameError(true)
      return
    }
    setMissingNameError(false)

    auth.send({
      type: 'resetPassword',
      token: action.token,
      password,
      passwordConfirmation,
      name: match(action.mode)
        .with('invite', () => name)
        .with('reset-password', () => undefined)
        .exhaustive(),
      cleanedPath: action.cleanedPath
    })
  }

  const state: ResetPasswordState = match({ missingNameError, recovery: authState.recovery })
    .returnType<ResetPasswordState>()
    .with({ missingNameError: true }, () => ({ status: 'failed', error: 'missing_name' }))
    .with({ recovery: { status: 'resettingPassword' } }, () => ({ status: 'running' }))
    .with({ recovery: { status: 'passwordResetSucceeded' } }, () => ({ status: 'succeeded' }))
    .with({ recovery: { status: 'passwordResetFailed' } }, ({ recovery }) => ({
      status: 'failed',
      error: recovery.error
    }))
    .otherwise(() => ({ status: 'idle' }))

  return (
    <div className="w-max-400 flex w-full flex-col gap-4">
      {header}

      <div className="flex flex-col items-stretch gap-5">
        {match(state)
          .with({ status: 'succeeded' }, () => (
            <>
              <p className="text-center text-base">Password set successfully. Please login.</p>
              <Button variant="primary" onClick={onClickContinueToLogin}>
                Continue to Login
              </Button>
            </>
          ))
          .with({ status: P.union('idle', 'running', 'failed') }, (state) => (
            <>
              {match(action.mode)
                .with('invite', () => (
                  <div className="text-base">Enter your name and set your password below.</div>
                ))
                .with('reset-password', () => (
                  <div className="text-base">Enter your new password below.</div>
                ))
                .exhaustive()}
              {action.mode == 'invite' && (
                <TextInput
                  placeholder="Full name"
                  type="text"
                  autoComplete="off"
                  value={name}
                  onChange={setName}
                />
              )}
              <TextInput
                placeholder="Password"
                type="password"
                autoComplete="off"
                value={password}
                onChange={setPassword}
              />
              <TextInput
                placeholder="Confirm password"
                type="password"
                autoComplete="off"
                value={passwordConfirmation}
                onChange={setPasswordConfirmation}
              />
              <Button variant="primary" onClick={onSubmit}>
                {match(state.status)
                  .with('running', () => 'Setting password...')
                  .otherwise(() => 'Set password')}
              </Button>
              {match(state)
                .with({ status: 'failed', error: 'missing_name' }, (state) => (
                  <p className="text-center text-base text-red-600">Please enter your name.</p>
                ))
                .with({ status: 'failed' }, ({ error }) => (
                  <p className="text-center text-base text-red-600">
                    {formatAuthError(error as AuthError)}
                  </p>
                ))
                .otherwise(() => null)}
            </>
          ))
          .exhaustive()}
      </div>
    </div>
  )
}

/**
 * Handles the MFA code input scenario.
 */
function MFAForm() {
  const auth = useProcess('auth')
  const authState = useAuthState()

  const [code, setCode] = useState('')

  if (authState.status != 'waitingForMFACode') return null

  function onSubmit() {
    auth.send({ type: 'completeLoginWithMFACode', code })
  }

  return (
    <div className="w-max-400 flex w-full flex-col gap-10">
      <div>Please enter the multi-factor auth code sent to {authState.mfa.session.description}</div>

      <div className="flex flex-col items-stretch gap-2">
        <input
          type="text"
          autoFocus={true}
          autoComplete="off"
          className="rounded-md border-0 py-1.5 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />

        <div className="flex flex-row items-start gap-2">
          <div className="flex-1">
            {match(authState.mfa)
              .with({ status: 'failed' }, ({ error }) => (
                <p className="text-base text-red-500">{formatAuthError(error)}</p>
              ))
              .otherwise(() => null)}
          </div>
        </div>
      </div>

      {match(authState.mfa.status)
        .with('verifying', () => (
          <Button variant="primary" disabled>
            Submitting...
          </Button>
        ))
        .with(P.union('idle', 'failed'), () => (
          <Button variant="primary" disabled={code == ''} onClick={onSubmit}>
            Submit
          </Button>
        ))
        .exhaustive()}
    </div>
  )
}

/**
 * Handles logging in with email/password.
 * If user needs MFA, `MFAForm` will take over rendering.
 * If user wants to reset password, `SendPasswordResetEmailForm` takes over.
 */
function LoginWithPasswordForm({
  header,
  onClickSignUp
}: {
  header: ReactNode
  onClickSignUp?: () => void
}) {
  const { send } = useProcess('auth')
  const authState = useAuthState()
  const [mode, setMode] = useState<'login' | 'resetPassword'>('login')

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const password = formData.get('password') as string
    send(loginWithEmailPassword({ email, password }))
  }

  // If waiting for MFA code, show MFAForm
  if (authState.status == 'waitingForMFACode') {
    return <MFAForm />
  }

  // If user requested password reset mode, show that form
  if (mode == 'resetPassword') {
    return <SendPasswordResetEmailForm header={header} />
  }

  return (
    <form className="w-max-400 flex w-full flex-col gap-10" onSubmit={onSubmit}>
      {header}

      <div className="flex flex-col items-stretch gap-5">
        <TextInput type="text" name="email" placeholder="Email" autoFocus={true} />

        <TextInput name="password" placeholder="Password" type="password" autoComplete="off" />

        {authState.status == 'loggingIn' ? (
          <Button disabled={true} variant="primary">
            Logging In
          </Button>
        ) : (
          <Button type="submit" variant="primary">
            Log In
          </Button>
        )}

        {match(authState)
          .with({ status: 'error' }, ({ error }) => (
            <p className="text-center text-base text-red-600">{formatAuthError(error)}</p>
          ))
          .otherwise(() => null)}
      </div>

      <div className="text-center">
        {"Don't have an account?"}{' '}
        {onClickSignUp ? (
          <button
            type="button"
            className="cursor-pointer text-blue-500 underline"
            onClick={onClickSignUp}
            aria-label="Sign up"
          >
            Sign up
          </button>
        ) : (
          <Link className="text-blue-500" href={{ name: 'sign_up' }}>
            Sign up
          </Link>
        )}
      </div>

      <div className="text-center text-sm">
        Forgot your password?{' '}
        <button
          type="button"
          className="cursor-pointer text-blue-500 underline"
          onClick={() => setMode('resetPassword')}
          aria-label="Reset password"
        >
          Click here
        </button>{' '}
        to reset it.
      </div>
    </form>
  )
}

/**
 * Handles sending a password reset email.
 */
function SendPasswordResetEmailForm({ header }: { header: ReactNode }) {
  type State =
    | { type: 'idle' }
    | { type: 'sending' }
    | { type: 'sent'; email: string }
    | { type: 'error'; error: PasswordResetEmailError }

  const auth = useProcess('auth')
  const authState = useAuthState()
  const [email, setEmail] = useState('')
  const state: State = match(authState.recovery)
    .returnType<State>()
    .with({ status: 'sendingEmail' }, () => ({ type: 'sending' }))
    .with({ status: 'emailSent' }, ({ email }) => ({ type: 'sent', email }))
    .with({ status: 'emailFailed' }, ({ error }) => ({ type: 'error', error }))
    .otherwise(() => ({ type: 'idle' }))

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    auth.send({
      type: 'sendPasswordResetEmail',
      email
    })
  }

  return (
    <form className="w-max-400 flex w-full flex-col gap-10" onSubmit={onSubmit}>
      {header}

      <div className="flex flex-col items-stretch gap-5">
        <TextInput
          autoFocus={true}
          type="text"
          placeholder="Email"
          value={email}
          onChange={setEmail}
        />

        {match(state)
          .with({ type: 'idle' }, () => (
            <Button type="submit" variant="primary">
              Reset password
            </Button>
          ))
          .with({ type: 'sending' }, () => (
            <Button disabled={true} variant="primary">
              Sending password reset email
            </Button>
          ))
          .with({ type: 'sent' }, ({ email }) => (
            <div className="text-base">
              Please check your email at {email} for the password reset link.
            </div>
          ))
          .with({ type: 'error' }, ({ error }) => (
            <div className="flex flex-col items-stretch gap-2">
              <Button type="submit" variant="primary">
                Reset password
              </Button>
              <p className="text-center text-base text-red-600">
                {formatResetPasswordError(error)}
              </p>
            </div>
          ))
          .exhaustive()}
      </div>
    </form>
  )
}

function formatResetPasswordError(error: PasswordResetEmailError): string {
  return match(error)
    .with('email_not_found', () => 'An account with the email address you provided was not found.')
    .with('request_failed', () => 'An unexpected error occurred. Please contact support.')
    .exhaustive()
}

/**
 * Headers and Branding
 */
function StanfordTaxBrandLogo() {
  return <BrandLogo className="text-3xl" href={envVar('NEXT_PUBLIC_HOMEPAGE_URL')} />
}

export function OrganizationHeader({ organization }: { organization: Organization | undefined }) {
  if (!organization) return <StanfordTaxHeader />

  return (
    <div className="flex flex-col items-center gap-3">
      <StanfordTaxBrandLogo />
      {organization.logo ? <OrganizationLogo src={organization.logo} size={80} /> : null}
      <h1 className="text-3xl">{organization.name}</h1>
    </div>
  )
}

export function StanfordTaxHeader() {
  return (
    <div className="flex flex-col items-center gap-3">
      <StanfordTaxBrandLogo />
    </div>
  )
}
